import { zodResolver } from '@hookform/resolvers/zod';
import { InlineAlertType } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import useVerifySsnChallenge, {
  INCORRECT_SSN_ERROR_CODE,
  THROTTLED_ERROR_CODE,
} from 'features/reactivation/hooks/useVerifySsnChallenge';
import { SSNVerificationSchema } from 'features/signup/SsnVerification/SsnVerification.schema';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { useUserProfile } from 'shared/hooks/useUserProfile';
import theme from 'shared/styles/theme';
import { formatSSN, SSN_MAX_LENGTH } from 'shared/utils/string/ssn';

type VerifySsnResult = { success: boolean };

const VerifySsn = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('reactivation', {
    keyPrefix: 'verifySsn',
  });
  const { bottom } = useSafeAreaInsets();
  useWindowDimensions();
  const isKeyboardVisible = useKeyboardVisibility();

  const { identityId } = useUserProfile();

  const { verifyChallenge, isLoading: isVerifyReactivationChallengeLoading } =
    useVerifySsnChallenge();

  const { segmentClient } = useAnalyticsConfiguration();
  const { challengeId: SSNChallengeId } = useLocalSearchParams();

  useViewDidFocusScreenAnalyticsEvent({
    key: 'verify-ssn-reactivation',
    customAttributes: {
      reactivation_flow: 'true',
    },
  });

  const [inlineErrorText, setInlineErrorText] = useState<string | null>(null);
  const [isSsnVisible, setIsSsnVisible] = useState(false);
  const handleSsnMaskClicked = usePressableAnalytics({
    analyticsId: 'ssnMaskClicked',
  });

  const {
    control,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { ssnText: '' },
    resolver: zodResolver(SSNVerificationSchema),
  });

  const handleBackButtonAction = React.useCallback(() => {
    router.replace('/reactivation');
    return true;
  }, [router]);

  useBackHandler(handleBackButtonAction);

  const onSubmitEditingAction = async () => {
    if (isValid) {
      await handleVerifySsn();
    }
  };

  const handleVerifySsn = async (): Promise<VerifySsnResult> => {
    if (!SSNChallengeId) {
      return { success: false };
    }

    setInlineErrorText(null);

    const formData = getValues();
    const ssn = formData.ssnText.replaceAll('-', '');

    const result = await verifyChallenge({
      challengeId: SSNChallengeId as string,
      identityId: identityId ?? '',
      acceptOptions: {
        ssnVerifyOption: {
          ssn,
        },
      },
    });

    if (result.success) {
      void segmentClient.logEvent('Registration Step Completed', {
        category: 'application',
        subcategory: 'checking',
        step_name: 'ssn_verification',
        reactivation_flow: 'true',
      });

      router.push('/reactivation/review-info');
      return { success: true };
    }

    if (!result.success) {
      const rawErrorCode = result.data?.errorCode;
      const errorCode =
        rawErrorCode !== null && !Number.isNaN(Number(rawErrorCode))
          ? Number(rawErrorCode)
          : null;

      if (errorCode === THROTTLED_ERROR_CODE) {
        router.push('/reactivation/verify-ssn-error');
      } else if (errorCode === INCORRECT_SSN_ERROR_CODE) {
        setInlineErrorText(translatedText('ssnMismatchError'));
      } else {
        setInlineErrorText(translatedText('errorMessageBody'));
      }
    }
    return { success: false };
  };

  useStepStartedFocusAnalytics({
    analyticsId: 'ssnVerificationStepStarted',
    customAttributes: {
      step_name: 'ssn_verification',
      reactivation_flow: 'true',
    },
  });

  return (
    <View style={styles.container} testID="inline-loader">
      <KeyboardAvoidingView
        style={styles.keyboardContainer}
        behavior={isIOS ? 'padding' : 'height'}
        keyboardVerticalOffset={isIOS ? 100 : 0}
      >
        <ScrollView contentContainerStyle={styles.keyboardContainer}>
          <Stack.Screen
            options={{
              headerBackVisible: true,
              headerShown: true,
              header: (props) => (
                <ExpoNavigationBar
                  {...props}
                  primaryOnPress={handleBackButtonAction}
                />
              ),
            }}
          />
          <View>
            {inlineErrorText ? (
              <View style={styles.inLineAlertContainer}>
                <InlineAlert
                  alertType={InlineAlertType.Error}
                  bodyText={inlineErrorText}
                  iconKeyType="ERROR"
                  onDismiss={() => setInlineErrorText(null)}
                />
              </View>
            ) : null}
          </View>
          <View style={styles.personalInfoFormContainer}>
            <TitleLgText numberOfLines={0} style={styles.heading}>
              {translatedText('title')}
            </TitleLgText>
            <BodyBaseText numberOfLines={0}>
              {translatedText('description')}
            </BodyBaseText>
            <Controller
              control={control}
              name="ssnText"
              render={({ field: { onChange, onBlur, value } }) => {
                const handleChange = (text: string) => {
                  if (inlineErrorText) {
                    setInlineErrorText(null);
                  }
                  onChange(formatSSN(text));
                  onBlur();
                };
                return (
                  <View style={styles.input}>
                    <VXLTextInput
                      testID="ssnField"
                      analyticsId="ssnFieldClicked"
                      labelText={translatedText('inputLabel')}
                      errorText={errors.ssnText?.message}
                      onChangeText={handleChange}
                      onBlur={onBlur}
                      value={value}
                      placeholder="___-__-____"
                      maxLength={SSN_MAX_LENGTH}
                      secureTextEntry={!isSsnVisible}
                      inputMode="numeric"
                      autoComplete="off"
                      onSubmitEditing={onSubmitEditingAction}
                      rightIcon={{
                        iconKey: !isSsnVisible ? 'VIEW' : 'HIDE',
                        iconAccessibilityLabel: isSsnVisible
                          ? 'eye-close'
                          : 'eye-open',
                        onPress: () => {
                          handleSsnMaskClicked();
                          setIsSsnVisible((currentValue) => !currentValue);
                        },
                      }}
                    />
                  </View>
                );
              }}
            />
          </View>
        </ScrollView>
        <View
          style={[
            styles.buttonContainer,
            {
              paddingBottom: isKeyboardVisible
                ? theme.spacing.xl
                : bottom + theme.spacing.m,
            },
          ]}
        >
          <Button
            label={translatedText('nextButton')}
            isDisabled={!isValid}
            isLoading={isVerifyReactivationChallengeLoading}
            onPress={handleVerifySsn}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    marginHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
  heading: {
    marginBottom: theme.spacing.m,
  },
  inLineAlertContainer: {
    padding: theme.spacing.m,
  },
  input: {
    marginTop: theme.spacing.m,
    width: '100%',
  },
  keyboardContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  personalInfoFormContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default VerifySsn;
